import React from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { OwnerVoiceFormControls, useOwnerVoiceForm, voiceFieldProps } from "../voice/OwnerVoiceOperatorContext";
import { normalizeVoiceText, parseVoiceDateTime } from "../voice/ownerVoiceParsers";

const DAYS = [[1,"Lunes"],[2,"Martes"],[3,"Miércoles"],[4,"Jueves"],[5,"Viernes"],[6,"Sábado"],[0,"Domingo"]];
const DOMAINS = [["personal","Personal"],["home","Hogar"],["freelance","Freelance"],["career","Carrera"],["business","Negocios"],["finance","Finanzas"],["other","Otro"]];

const DAY_VOICE = { domingo: 0, lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6 };

function parseRoutineDays(value) {
  const text = normalizeVoiceText(value);
  if (!text) return null;
  if (text.includes("todos los dias") || text.includes("cada dia")) return [0, 1, 2, 3, 4, 5, 6];
  if (text.includes("dias de semana") || text.includes("lunes a viernes")) return [1, 2, 3, 4, 5];
  if (text.includes("fin de semana") || text.includes("sabado y domingo")) return [6, 0];
  const days = Object.entries(DAY_VOICE).filter(([name]) => text.includes(name)).map(([, value]) => value);
  return days.length ? Array.from(new Set(days)) : null;
}

function parseRoutineClock(value) {
  const parsed = parseVoiceDateTime(`hoy ${String(value || "")}`);
  return parsed ? parsed.slice(11, 16) : null;
}

function Actions({ saving, onClose, onSubmit, label }) {
  return <DialogActions sx={{ px: 3, pb: 2.3 }}><Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button><Button variant="contained" onClick={onSubmit} disabled={saving} sx={{ textTransform: "none", fontWeight: 950 }}>{saving ? "Guardando…" : label}</Button></DialogActions>;
}

export function OwnerRoutineDialog({ open, onClose, selectedDate, onSaved, initialValues = null }) {
  const [form, setForm] = React.useState({ name: "", domain: "personal", weekdays: [1,2,3,4,5], startTime: "08:00", endTime: "09:00", startDate: "", endDate: "", notes: "" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    if (!open) return;
    setForm((current) => ({ ...current, startDate: selectedDate || "", ...(initialValues && typeof initialValues === "object" ? initialValues : {}) }));
  }, [open, selectedDate, initialValues]);

  useOwnerVoiceForm({
    id: "routine",
    open: Boolean(open),
    config: {
      title: "rutina",
      values: form,
      setValue: (name, value) => setForm((current) => ({ ...current, [name]: value })),
      fields: [
        { name: "name", label: "rutina", prompt: "¿Cómo querés llamar a esta rutina?", required: true, aliases: ["nombre", "rutina"] },
        { name: "weekdays", label: "días", prompt: "¿Qué días la hacés? Por ejemplo lunes miércoles y viernes, días de semana o todos los días.", required: true, parse: parseRoutineDays, aliases: ["dias", "días", "frecuencia"] },
        { name: "startTime", label: "hora de inicio", prompt: "¿A qué hora empieza?", required: true, parse: parseRoutineClock, aliases: ["inicio", "empieza", "desde"] },
        { name: "endTime", label: "hora de fin", prompt: "¿A qué hora termina?", required: true, parse: parseRoutineClock, aliases: ["fin", "termina", "hasta"] },
        { name: "domain", label: "área", prompt: "¿En qué área entra?", kind: "choice", required: true, options: DOMAINS.map(([value, label]) => ({ value, label })), aliases: ["area", "área", "categoria", "categoría"] },
        { name: "notes", label: "notas", prompt: "¿Querés agregar una nota? Si no, decí siguiente.", required: false, aliases: ["nota", "notas", "detalle"] },
      ],
      onSubmit: () => submit(),
      onCancel: onClose,
      successMessage: "Rutina creada.",
    },
  });

  async function submit() {
    setSaving(true); setError("");
    try {
      await ownerApi.createRoutine({ ...form, startDate: form.startDate || null, endDate: form.endDate || null });
      await onSaved?.();
      onClose?.();
      return true;
    } catch (err) {
      setError(formatApiError(err, "No pude guardar la rutina."));
      return false;
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 1000 }}>Nueva rutina</DialogTitle>
      <DialogContent>
        <Stack spacing={1.4} sx={{ pt: .5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField inputProps={voiceFieldProps("name")} autoFocus label="Rutina" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej.: Entrenamiento, planificación, almuerzo" />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <TextField fullWidth type="time" label="Desde" InputLabelProps={{ shrink: true }} inputProps={voiceFieldProps("startTime")} value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
            <TextField fullWidth type="time" label="Hasta" InputLabelProps={{ shrink: true }} inputProps={voiceFieldProps("endTime")} value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
          </Stack>
          <FormControl fullWidth>
            <InputLabel>Días</InputLabel>
            <Select inputProps={voiceFieldProps("weekdays")} multiple label="Días" value={form.weekdays} onChange={(e) => setForm({ ...form, weekdays: e.target.value.map(Number) })} renderValue={(selected) => DAYS.filter(([value]) => selected.includes(value)).map(([,label]) => label.slice(0,3)).join(" · ")}>
              {DAYS.map(([value,label]) => <MenuItem key={value} value={value}><Checkbox checked={form.weekdays.includes(value)} /><ListItemText primary={label} /></MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Área</InputLabel>
            <Select inputProps={voiceFieldProps("domain")} label="Área" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })}>
              {DOMAINS.map(([value,label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <TextField fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
            <TextField fullWidth type="date" label="Hasta (opcional)" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
          </Stack>
          <TextField inputProps={voiceFieldProps("notes")} multiline minRows={2} label="Notas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <OwnerVoiceFormControls label="Crear rutina hablando" />
        </Stack>
      </DialogContent>
      <Actions saving={saving} onClose={onClose} onSubmit={submit} label="Crear rutina" />
    </Dialog>
  );
}

export function OwnerCalendarSettingsDialog({ open, onClose, settings, onSaved }) {
  const [form, setForm] = React.useState({ workdayStart: "09:00", workdayEnd: "18:00", workdays: [1,2,3,4,5] });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  React.useEffect(() => { if (open) setForm({ workdayStart: settings?.workdayStart || "09:00", workdayEnd: settings?.workdayEnd || "18:00", workdays: settings?.workdays || [1,2,3,4,5] }); }, [open, settings]);
  const submit = async () => { setSaving(true); setError(""); try { await ownerApi.updateCalendarSettings(form); onSaved?.(); onClose?.(); } catch (err) { setError(formatApiError(err, "No pude actualizar tu jornada.")); } finally { setSaving(false); } };
  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm"><DialogTitle sx={{ fontWeight: 1000 }}>Jornada disponible</DialogTitle><DialogContent><Stack spacing={1.4} sx={{ pt: .5 }}>{error ? <Alert severity="error">{error}</Alert> : null}<Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".82rem" }}>Esto define tu capacidad freelancer. Rutinas y tareas agendadas se descuentan de esta ventana; tareas sin duración no inventan tiempo ocupado.</Typography><Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}><TextField fullWidth type="time" label="Empiezo" InputLabelProps={{ shrink: true }} value={form.workdayStart} onChange={(e) => setForm({ ...form, workdayStart: e.target.value })} /><TextField fullWidth type="time" label="Termino" InputLabelProps={{ shrink: true }} value={form.workdayEnd} onChange={(e) => setForm({ ...form, workdayEnd: e.target.value })} /></Stack><FormControl fullWidth><InputLabel>Días de trabajo</InputLabel><Select multiple label="Días de trabajo" value={form.workdays} onChange={(e) => setForm({ ...form, workdays: e.target.value.map(Number) })} renderValue={(selected) => DAYS.filter(([value]) => selected.includes(value)).map(([,label]) => label.slice(0,3)).join(" · ")}>{DAYS.map(([value,label]) => <MenuItem key={value} value={value}><Checkbox checked={form.workdays.includes(value)} /><ListItemText primary={label} /></MenuItem>)}</Select></FormControl></Stack></DialogContent><Actions saving={saving} onClose={onClose} onSubmit={submit} label="Guardar jornada" /></Dialog>;
}
